import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';
import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import Icon from '@components/icon';
import Link from '@components/link';

// ---------------------------------------------------------

import {
  dropdown_links_heading,
  dropdown_is_showing,
  dropdown_link_is_active,
  dropdown_menu_inner,
  dropdown_menu,
  dropdown,
  horizontal_sections,
  my_account_dropdown,
  my_account_dropdown_horizontal
} from './styles.module.scss';

// ---------------------------------------------------------

const Dropdown = (props) => {
  let { items, label, isFullNavigation, theme, menuIsOpen } = props;

  // ---------------------------------------------------------

  const router = useRouter();
  const [dropdownHeight, setDropdownHeight] = useState(isFullNavigation ? 0 : 'auto');
  const [clicked, setClicked] = useState(false);

  // Render sections side-by-side when more than one is supplied.
  const isHorizontal = items.length > 1;

  // ---------------------------------------------------------

  const classes = classNames(dropdown_menu, {
    [my_account_dropdown]: theme === 'my-account',
    [my_account_dropdown_horizontal]: theme === 'my-account' && isHorizontal
  });

  // ---------------------------------------------------------

  const closeDropdown = () => {
    setDropdownHeight(0);
    setClicked(false);
  };

  useEffect(() => {
    const handleRouteChange = () => {
      if (clicked) {
        closeDropdown();
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [clicked, router]);

  return (
    <OutsideClickHandler onOutsideClick={closeDropdown}>
      <button
        aria-expanded={dropdownHeight !== 0}
        aria-controls={label}
        className={classNames(dropdown, {
          [dropdown_is_showing]: clicked
        })}
        onClick={() => {
          setClicked(!clicked);
          setDropdownHeight((prev) => (prev === 0 ? 'auto' : 0));
        }}
      >
        {label}
        <Icon name="small-down" type="arrow" />
      </button>

      <AnimateHeight
        animateOpacity={true}
        className={classes}
        contentClassName={dropdown_menu_inner}
        duration={isFullNavigation ? 300 : 0}
        height={isFullNavigation && clicked ? 'auto' : menuIsOpen && !isFullNavigation ? 'auto' : 0}
        id={label}
      >
        <div className={isHorizontal && isFullNavigation ? horizontal_sections : ''}>
          {items.map((item, index) => {
            return (
              <ul key={`dropdown-section-${index}`}>
                {item.heading && (
                  <li className={dropdown_links_heading}>
                    <span>{item.heading}</span>
                  </li>
                )}
                {item.children.map(
                  (child) =>
                    child.url && (
                      <li key={`${child.label}`} className={child.className}>
                        {child.url.includes('logout') ? (
                          <a href={child.url}>{child.label}</a>
                        ) : (
                          <Link
                            forceAnchorTag={child.forceAnchorTag}
                            activeClassName={dropdown_link_is_active}
                            url={child.url}
                            query={child.query}
                          >
                            {child.label}
                          </Link>
                        )}
                      </li>
                    )
                )}
              </ul>
            );
          })}
        </div>
      </AnimateHeight>
    </OutsideClickHandler>
  );
};

Dropdown.propTypes = {
  isFullNavigation: PropTypes.bool,
  items: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  menuIsOpen: PropTypes.bool,
  theme: PropTypes.string
};

export default Dropdown;
